import { filter, orderBy, map, uniq, some } from 'lodash-es';
import moment from 'moment';
import * as DateTool from '../../../services/DateToolService';

/**
 * Retrieves a list of filtered allocations
 *
 * @param state
 * @returns {*|Array}
 */
export const filteredAccommodations = (state) => {
    const _selected = state.filters.selected;
    let _allocations = state.list.items;
    let _predicate = state.list.predicate;
    let _order = state.list.order;

    _allocations = filter(_allocations, (allocation) => {
        const site = _selected.site ? allocation.site === _selected.site : true;
        const accommodation = _selected.accommodation ? allocation.name === _selected.accommodation : true;

        return site && accommodation;
    });

    return orderBy(_allocations, [_predicate], [_order]);
};

/**
 * Retrieves a list of filtered allocations sliced by the current
 * page and limit.
 *
 * @param state
 * @param getters
 */
export const filteredAccommodationsPaginated = (state, getters) => {
    let _page = state.list.page;
    let _limit = state.list.limit;

    return getters.filteredAccommodations.slice(_page * _limit - _limit, _page * _limit);
};

/**
 * Retrieves the maximum number of pages for the currently
 * filtered allocations
 *
 * @param state
 * @param getters
 * @returns {number}
 */
export const filteredAccommodationsMaxPages = (state, getters) => {
    let _limit = state.list.limit;
    return Math.ceil(getters.filteredAccommodations.length / _limit);
};

/**
 * Filters the allocation list based on show/hide historical data.
 *
 * @param state
 * @returns {Array}
 */
export const filteredAllocations = (state) => {
    const _today = moment().startOf('day');
    let _display = state.list.display;

    if(state.allocations.items) {
        const accommodationSiteId = Object.keys(state.allocations.items)[0];
        let allocationsList = map(state.allocations.items[accommodationSiteId]);
        return filter(allocationsList, (allocation) => {
            switch(_display) {
            case 'current':
                return _today.diff(DateTool.convertStringDateToDate(allocation.to)) <= 0;
            case 'historical':
                return _today.diff(DateTool.convertStringDateToDate(allocation.to)) > 0;
            default:
                return true
            }
        });
    }
};

/**
 * Retrieves a paginated list of the filtered bookings
 *
 * @param state
 * @returns array
 */
export const filteredAllocationsPaginated = (state) => {
    let _page = state.allocations.page;
    let _limit = state.allocations.limit;
    let _allocationList = filteredAllocations(state);

    if(state.allocations.items) {
        return _allocationList.slice(_page * _limit - _limit, _page * _limit);
    }
    return [];
};

/**
 * Retrieves the maximum number of pages returned
 * from the paginated list of filtered bookings
 *
 * @param state
 * @returns {number}
 */
export const filteredAllocationsMaxPages = (state) => {
    let _allocationList = filteredAllocations(state);
    let _limit = state.allocations.limit;
    let _maxPage = 1;

    if(state.allocations.items) {
        _maxPage = Math.ceil(_allocationList.length / _limit);
        return _maxPage > 0 ? _maxPage : 1;
    }
    return _maxPage;
};

/**
 * Retrieves a list of site names from the nested
 * site / accommodations object
 *
 * @param state
 */
export const siteNames = (state) => {
    return map(state.filters.sites, 'name');
};

/**
 * Retrieve a list of accommodation names from the selected site.
 *
 * @param state
 * @returns {Array}
 */
export const accommodations = (state) => {
    let _site = filter(state.filters.sites, (site) => {
        if (site.name === state.filters.selected.site) {
            return site;
        }
    });
    let _accommodations = map(_site, 'accommodations');
    return map(_accommodations[0], 'name')
};

/**
 * Retrieves a list of site names from the nested
 * site / accommodations object, for the route changer
 * on the manage allocations page
 */
export const routeSiteNames = (state) => {
    return state.form.filters.sites || [];
};

/**
 * Retrieve a list of the accommodation names belonging to the site chosen.
 *
 * @param state
 * @returns {Array}
 */
export const routeAccommodationNames = (state) => {
    const sites = state.form.filters.sites || [];
    const selectedSiteName = state.form.filters.selected.site ?
        state.form.filters.selected.site.name : '';
    let site = filter(sites, (site) => {
        return site.name === selectedSiteName;
    });
    return site.length ? site[0].accommodations : [];
};

/**
 * Get list of allocations that have parts within a given period
 *
 * @param state
 * @returns {Array}
 */
export const allocationsInPeriod = (state) => {
    let allocationsWithinPeriod = [];
    let from = state.calendar.from;
    let to = state.calendar.to;

    let allocations = (state.allocationsMerged.items) ? state.allocationsMerged.items : [];
    let changeRequests = (state.request.items && state.request.items.pending) ? state.request.items.pending : [];
    // If the dates are set then check within periods which allocations that are present.
    if (from && to) {
        allocationsWithinPeriod = DateTool.listOfAllocationsWithinPeriod(from, to, allocations, changeRequests)
    }
    return allocationsWithinPeriod;
};

/**
 * Get list of allocations that have parts within a given period - paginated.
 *
 * @param state
 * @param getters
 * @returns {Array}
 */
export const allocationsInPeriodPaginated = (state, getters) => {
    let _page = state.modal.list.page;
    let _limit = state.modal.list.limit;
    let allocationsWithinPeriod = getters.allocationsInPeriod;

    return map(allocationsWithinPeriod.slice(_page * _limit - _limit, _page * _limit));
};

/**
 * Retrieves the maximum number of pages for the currently
 * filtered allocations
 *
 * @param state
 * @param getters
 * @returns {number}
 */
export const allocationsInPeriodMaxPages = (state, getters) => {
    let _limit = state.modal.list.limit;
    return Math.ceil(getters.allocationsInPeriod.length / _limit);
};

/**
 * Check all allocation within period to see if any have allotment on them. If all are blocked or on request return true.
 *
 * @param state
 * @param getters
 * @returns {boolean}
 */
export const allotmentInPeriod = (state, getters) => {
    let allotmentInPeriod = true;
    let allocationsInPeriod = getters.allocationsInPeriod;
    if(allocationsInPeriod.length > 0) {
        allocationsInPeriod.forEach((item) => {
            allotmentInPeriod = !(item.allocation.type === 'BLOCKED'
                || item.allocation.type === 'REQUEST'
                || (item.isReleased && item.allocation.releaseType === 'BLOCKED')
                || (item.isReleased && item.allocation.releaseType === 'REQUEST'));
        })
    }
    return allotmentInPeriod;
};

/**
 * Check if any attributes has been changed in the overlay alllocation.
 *
 * @param state
 * @returns {boolean}
 */
export const overlayAllocationChanged = (state) => {
    const item = state.modal.overlay.allocation.item;
    const original = state.modal.overlay.allocation.original;
    let changed = false;

    if (item) {
        filter(item, (attribute, key) => {
            // Check if any attribute has changed.
            if (attribute !== original[key]) {
                changed = true;
            }
        })
    }
    return changed;
};

/**
 * Return the calendar for the calendar view.
 *
 * @param state
 */
export const getCalendar = (state) => {
    // If accommodations exists then generate the calendar
    if (state.calendar.items !== undefined) {
        // Set initial values
        let calendar = [];
        let index = 0;
        let weekIndex = 0;
        let monthIndex = 0;
        const dates = state.calendar.items.dates;
        // Generate initial dates
        const today = moment().startOf('day');
        const current = state.allocations.years.current;
        const startOfYear = moment('01/01/' + current, 'DD/MM/YYYY');
        const endOfYear = moment('31/12/' + current, 'DD/MM/YYYY').add(1, 'days');

        // Run through the year to generate a yearly calendar with months, weeks and dates.
        while(startOfYear.diff(endOfYear) < 0) {
            // If new month then generate an array to store weeks
            if(!calendar[startOfYear.month()]){
                calendar[startOfYear.month()] = {
                    index: monthIndex,
                    weeks: []
                };
                monthIndex++;
                weekIndex = 0;
            }
            // If new week then generate a new week to store days
            if(!calendar[startOfYear.month()].weeks[weekIndex]) {
                // Fill array with 7 elements to compensate for days in week not i current month
                calendar[startOfYear.month()].weeks[weekIndex] = {
                    number: startOfYear.week(),
                    days: [
                        {day: null},
                        {day: null},
                        {day: null},
                        {day: null},
                        {day: null},
                        {day: null},
                        {day: null}
                    ]
                }
            }
            // Push dates
            calendar[startOfYear.month()].weeks[weekIndex].days[startOfYear.day()].day = startOfYear.clone();
            // Set an index on each date to use for javascript rendering animations
            calendar[startOfYear.month()].weeks[weekIndex].days[startOfYear.day()].index = index;
            // Set the tooltip data
            calendar[startOfYear.month()].weeks[weekIndex].days[startOfYear.day()].tooltip = {initialUnits: 12, currentUnits: 10, arrivalData: false};
            // Set the data for the current date
            calendar[startOfYear.month()].weeks[weekIndex].days[startOfYear.day()].data = (dates[index]) ? dates[index] : null;
            // Set boolean to indicate if day is past
            calendar[startOfYear.month()].weeks[weekIndex].days[startOfYear.day()].past = today.diff(startOfYear) > 0;
            // Set the current day boolean
            calendar[startOfYear.month()].weeks[weekIndex].days[startOfYear.day()].currentDay = today.diff(startOfYear) === 0;
            // Increment index
            index++;
            // Go to next day
            startOfYear.add(1, 'days');
            // If new week then increment weekIndex
            if (startOfYear.day() === 0) {
                weekIndex++;
            }
        }
        return calendar;
    }
    return null;
};

/**
 * Paginate the calendar. The initial months are set from april to september
 *
 * @param state
 * @param getters
 * @returns {Array}
 */
export const getCalendarPaginated = (state, getters) => {
    const calendar = getters.getCalendar;
    const startMonth = state.calendar.startMonth;
    const endMonth = state.calendar.endMonth;
    return filter(calendar, (month, index) => {
        if (index >= startMonth && index < endMonth) {
            return month;
        }
    });
};

/**
 * Count the amount of requests.
 *
 * @param state
 * @returns {*}
 */
export const requestCount = (state) => {
    return state.request.items ? [
        state.request.items.pending.length,
        state.request.items.approved.length,
        state.request.items.denied.length,
        state.request.items.modified.length,
    ] : [];
};

/**
 * Return list of change requests for the allocation.
 *
 * @param state
 * @returns {*}
 */
export const changeRequestForAllocation = (state) => {
    const allocation = state.modal.overlay.allocation.original;
    const changeRequests = (state.request.items && state.request.items.pending.length) ? state.request.items.pending : [];
    let changeRequest = [];
    if(allocation && changeRequests) {
        changeRequest = DateTool.changeRequestForAllocation(allocation, changeRequests);
    }
    return changeRequest;
};

/**
 * Return list of requests paginated.
 *
 * @param state
 * @returns {Array}
 */
export const requestsPaginated = (state) => {
    const _predicate = state.request.predicate;
    const _page = state.request.page;
    const _limit = state.request.limit;
    const requests = state.request.items && state.request.items[_predicate] ? state.request.items[_predicate] : [];

    return map(requests.slice(_page * _limit - _limit, _page * _limit));
};

/**
 * Return max page count for requests.
 *
 * @param state
 * @returns {number}
 */
export const requestsMaxPages = (state) => {
    const _predicate = state.request.predicate;
    const _limit = state.request.limit;
    const requests = state.request.items && state.request.items[_predicate] ? state.request.items[_predicate] : [];

    return Math.ceil(requests.length / _limit);
};

/**
 * Get a suppliers sites.
 *
 * @param state
 * @param getters
 * @param rootState
 */
export const filteredSites = (state) => {
    const allocations = state.list.items;
    return uniq(map(allocations, (allocation) => {
        return allocation.site;
    }));
};

/**
 * Return list of allocation types.
 *
 * @returns {string[]}
 */
export const getAllocationTypes = () => {
    return [
        'ALLOTMENT',
        'FREESALE',
        'REQUEST'
    ]
};

/**
 * Return list of allocation release types.
 *
 * @returns {string[]}
 */
export const getAllocationReleaseTypes = () => {
    return [
        'FREESALE',
        'REQUEST',
        'BLOCKED'
    ]
};

/**
 * Get allocation statistics for a given period.
 *
 * @param state
 */
export const getStatisticsForPeriod = (state) => {
    const calendar = state.calendar.items;
    const allocation = state.modal.overlay.allocation.original;
    const arrivalRules = state.arrivalRules.item;
    let statisticsObject = {
        bookings: false,
        bookingsMaxCount: 0,
        arrival: false,
        arrivalRules: []
    };

    if (allocation && calendar && arrivalRules) {
        let startIndex = moment(allocation.from).dayOfYear();
        let endIndex = moment(allocation.to).dayOfYear();

        for (startIndex; startIndex < endIndex; startIndex++) {
            const day = calendar.dates[startIndex - 1];
            // If bookings then set statisticsObject.bookings to true
            if (!statisticsObject.bookings && day.bookings) {
                statisticsObject.bookings = true;
                // Check and set statisticsObject.bookingsMaxCount
                if (day.bookings > statisticsObject.bookingsMaxCount) {
                    statisticsObject.bookingsMaxCount = day.bookings;
                }
            }
            // If arrival then set statisticsObject.arrival to true
            if (!statisticsObject.arrival && day.arrival) {
                statisticsObject.arrival = true;
            }
            // If new arrival rule then add to statisticsObject.arrivalRules
            if (day.minStayDays || day.maxStayDays) {
                statisticsObject.arrivalRules = getArrivalRulesFromPeriod(
                    statisticsObject.arrivalRules,
                    day,
                    arrivalRules
                )
            }
        }
    }
    return statisticsObject;
};

/**
 * Generate and add new arrival rule.
 *
 * @param statisticsObjectArrivalRules
 * @param arrivalRules
 * @param day
 */
const getArrivalRulesFromPeriod = (statisticsObjectArrivalRules, day, arrivalRules) => {
    const arrivalRuleOnDay = filter(arrivalRules, (rule) => {
        return moment(day.date).isBetween(moment(rule.startDate), moment(rule.endDate).add(1, 'days'));
    })[0];
    if (statisticsObjectArrivalRules.length === 0) {
        statisticsObjectArrivalRules.push(arrivalRuleOnDay)
    } else {
        // Check if arrival rule already exists
        if (!some(statisticsObjectArrivalRules, arrivalRuleOnDay)) {
            statisticsObjectArrivalRules.push(arrivalRuleOnDay)
        }
    }
    return statisticsObjectArrivalRules;
};



// WEBPACK FOOTER //
// ./src/store/modules/allocation/getters.js